import type { DrawingKind } from "lightweight-charts-drawing"
import { Option, Schema } from "effect"
import { useSyncExternalStore } from "react"
import { documentSaver, loadDocument } from "../../documents.ts"
import { type BarPosition, decodeFavorites, toggleFavorite } from "./toolFavoritesModel.ts"

// The user's favourite drawing tools (toolFavoritesModel.ts), saved for them ("drawing-favorites"
// document) so they follow them across browsers; this browser keeps a copy per user.

const SERVER_KEY = "drawing-favorites"
const localKey = (userId: string) => `trader.drawingFavorites.v1.${userId}`

let userId: string | null = null
let favorites: ReadonlyArray<DrawingKind> = []
let edits = 0
const listeners = new Set<() => void>()
const saver = documentSaver(800)
const encode = (list: ReadonlyArray<DrawingKind>) => JSON.stringify({ version: 1, tools: list })

const set = (next: ReadonlyArray<DrawingKind>, save: boolean) => {
  favorites = next
  if (userId !== null) {
    try {
      localStorage.setItem(localKey(userId), encode(next))
    } catch {
      // Not cached here; the server copy still is.
    }
  }
  if (save) {
    edits++
    saver.schedule(SERVER_KEY, () => encode(favorites))
  }
  for (const listener of listeners) listener()
}

export const toolFavorites = {
  /** After sign-in: this browser's copy at once, then the server's (unless the user starred meanwhile). */
  signedIn: async (id: string): Promise<void> => {
    if (userId !== null && userId !== id) saver.discard()
    userId = id
    let local: ReadonlyArray<DrawingKind> | null = null
    try {
      local = decodeFavorites(localStorage.getItem(localKey(id)))
    } catch {
      local = null
    }
    set(local ?? [], false)
    const before = edits
    const saved = decodeFavorites(await loadDocument(SERVER_KEY).catch(() => null))
    if (userId === id && saved !== null && edits === before) set(saved, false)
  },
  get: (): ReadonlyArray<DrawingKind> => favorites,
  toggle: (kind: DrawingKind): void => set(toggleFavorite(favorites, kind), true),
  subscribe: (listener: () => void) => {
    listeners.add(listener)
    return () => {
      listeners.delete(listener)
    }
  },
  flush: (): Promise<void> => saver.flush()
}

export const useToolFavorites = (): ReadonlyArray<DrawingKind> => useSyncExternalStore(toolFavorites.subscribe, toolFavorites.get)

const decodePosition = Schema.decodeUnknownOption(Schema.fromJsonString(Schema.Struct({ left: Schema.Finite, top: Schema.Finite })))
const POSITION_KEY = "trader.favoriteTools.position"

/** Where the user left the bar in this browser; null until they move it (it starts top-centre). */
export const favoritesBarPosition = {
  load: (): BarPosition | null => {
    try {
      return Option.getOrNull(decodePosition(localStorage.getItem(POSITION_KEY) ?? ""))
    } catch {
      return null
    }
  },
  save: (position: BarPosition): void => {
    try {
      localStorage.setItem(POSITION_KEY, JSON.stringify(position))
    } catch {
      // Not remembered; the bar still stays put until the page reloads.
    }
  }
}
